import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Brand names stay as is; 'other' (and unknown types) fall back to t('cardTypeOther')
const TYPE_LABELS = { uzcard: 'Uzcard', humo: 'Humo', visa: 'Visa', mastercard: 'Mastercard' };

// "8600123412341234" -> "8600 1234 1234 1234"
const groupDigits = (value) => value.replace(/\D/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');

const emptyForm = { card_number: '', holder_name: '', label: '' };

/**
 * "To'lov kartalarim": cards this user receives debt repayments on. Debtors
 * see the active ones in the Telegram bot when they choose to pay, and
 * incoming transfers are recognised by the card's last 4 digits.
 */
export default function PaymentCardsCard() {
  const { t } = useTranslation('paymentCards');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/my/payment-cards`, { withCredentials: true });
      setCards(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const addCard = async () => {
    const digits = form.card_number.replace(/\D/g, '');
    if (digits.length < 16) { toast.error(t('validation.cardNumberLength')); return; }
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/my/payment-cards`, { ...form, card_number: digits }, { withCredentials: true });
      toast.success(t('toast.added'));
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.addFailed'));
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (card) => {
    try {
      await axios.patch(`${API_URL}/api/my/payment-cards/${card.id}`, { is_active: !card.is_active }, { withCredentials: true });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common:status.error'));
    }
  };

  const removeCard = async (card) => {
    if (!window.confirm(t('confirm.delete', { last4: card.last4 }))) return;
    try {
      await axios.delete(`${API_URL}/api/my/payment-cards/${card.id}`, { withCredentials: true });
      toast.success(t('toast.deleted'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common:status.error'));
    }
  };

  return (
    <Card data-testid="payment-cards">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><CreditCard className="h-5 w-5" /> {t('title')}</span>
          {!showForm && (
            <Button size="sm" variant="outline" onClick={() => setShowForm(true)} className="gap-1.5">
              <Plus className="h-4 w-4" /> {t('addCard')}
            </Button>
          )}
        </CardTitle>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('description')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {showForm && (
          <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 space-y-3">
            <div className="space-y-1.5">
              <Label>{t('form.cardNumber')}</Label>
              <Input
                inputMode="numeric"
                autoComplete="off"
                placeholder="8600 0000 0000 0000"
                value={groupDigits(form.card_number)}
                onChange={e => setForm(f => ({ ...f, card_number: e.target.value }))}
                data-testid="payment-card-number"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t('form.holderName')}</Label>
                <Input placeholder={t('form.holderNamePlaceholder')} value={form.holder_name}
                  onChange={e => setForm(f => ({ ...f, holder_name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('form.label')}</Label>
                <Input placeholder={t('form.labelPlaceholder')} value={form.label}
                  onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setForm(emptyForm); }} disabled={adding}>
                {t('common:actions.cancel')}
              </Button>
              <Button size="sm" onClick={addCard} disabled={adding} data-testid="payment-card-save">
                {adding && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} {t('common:actions.save')}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> {t('common:status.loading')}</div>
        ) : cards.length === 0 ? (
          !showForm && <p className="text-sm text-slate-500 dark:text-slate-400">{t('empty')}</p>
        ) : (
          <div className="space-y-2">
            {cards.map(card => (
              <div key={card.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 ${card.is_active ? '' : 'opacity-60'}`}>
                <div className="min-w-0">
                  <div className="font-mono text-sm text-slate-900 dark:text-slate-100">{groupDigits(card.card_number)}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {TYPE_LABELS[card.card_type] || t('cardTypeOther')}
                    {card.label && ` · ${card.label}`}
                    {card.holder_name && ` · ${card.holder_name}`}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Switch checked={card.is_active} onCheckedChange={() => toggleActive(card)} />
                    {card.is_active ? t('common:status.active') : t('inactive')}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeCard(card)} className="h-8 px-2 text-red-500 hover:text-red-700" title={t('common:actions.delete')}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
